import SpanishFlag from '@assets/flag_spain.svg';
import EnglishFlag from '@assets/flag_usa.svg';
import ActionMessages from '@components/ActionMessages';
import LoginForm from '@components/LoginForm';
import LoginPromo from '@components/LoginPromo';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { getSelfWithOrganizationRequest, UserLocale } from '@models/user.model';
import { Box, Grid, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { logoutUser, setUserData, setUserDetails, setUserOrganizations } from '@redux/reducers/user.reducer';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isLoggedIn } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const user = useAppSelector((state: RootState) => state.user);
  const { activeLocale } = useAppSelector((state: RootState) => state.locale);

  // Redirect logged-in users away from login page
  useEffect(() => {
    if (isLoggedIn && organizationId) {
      history.replace('/areas');
    }
  }, [isLoggedIn, organizationId, history]);

  const [showReset, setShowReset] = useState<boolean>(false);
  const [showConfirmUser, setShowConfirmUser] = useState<boolean>(false);
  const [showInitial, setShowInitial] = useState<boolean>(false);
  const [confirmToken, setConfirmToken] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [prefilledEmail, setPrefilledEmail] = useState<string>('');
  const [prefilledCode, setPrefilledCode] = useState<string>('');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Get locale from URL query param only (returns null if not present)
  const getLocaleFromUrl = (): UserLocale | null => {
    const url = window.location.href;
    const localeParam = url.match(/[?&]locale=([^&]+)/)?.[1]?.toLowerCase();
    if (localeParam && ['en', 'es'].includes(localeParam)) {
      return localeParam as UserLocale;
    }
    return null;
  };

  // Get locale from URL query param or fall back to browser language
  const getLocaleFromUrlOrBrowser = (): UserLocale => {
    const urlLocale = getLocaleFromUrl();
    if (urlLocale) {
      return urlLocale;
    }
    const browserLang = navigator.language.split('-')[0];
    return ['en', 'es'].includes(browserLang) ? (browserLang as UserLocale) : UserLocale.EN;
  };

  // Handle locale from URL param - also for already logged-in users
  useEffect(() => {
    const urlLocale = getLocaleFromUrl();
    const locale = urlLocale || getLocaleFromUrlOrBrowser();
    dispatch(setLocale(locale));
    // Store URL locale in sessionStorage (only if from URL flag, for use after login)
    if (urlLocale) {
      sessionStorage.setItem('urlLocale', urlLocale);
    } else {
      sessionStorage.removeItem('urlLocale');
    }

    // If user is already logged in and there's a locale param, update their locale and redirect
    if (user.isLoggedIn && urlLocale) {
      const updateLocaleAndRedirect = async () => {
        try {
          await graphQlClient.request(UserGraphQL.mutations.updateOwnUser, {
            user: { locale: urlLocale },
          });
        } catch (error) {
          console.error('Failed to save user locale preference:', error);
        }
        history.push('/');
      };
      updateLocaleAndRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user.isLoggedIn, history]);

  // Extract email and code from URL parameters on component mount
  // This allows pre-filling login fields from URL parameters
  // Example: /login?email=user@example.com&code=abc123
  // Note we do not use query because we might have non-encoded URL parameters
  useEffect(() => {
    const url = window.location.href;
    const regex = /[?&]code=([^&]+(?:&(?!email)[^&]+)*)&email=([^&]+)/;
    const match = url.match(regex);

    if (match) {
      const code = match[1];
      const email = match[2];
      setPrefilledEmail(email);
      setPrefilledCode(code);
    }
  }, []);

  const forgotPassword = async (email: string) => {
    setErrorMessage('');
    if (!email) {
      return dispatch(
        appendActionMessage({
          message: localeActionMessages['loginNoEmail'],
          type: SnackType.ERROR,
        }),
      );
    }

    const url = process.env.REACT_APP_BACKEND_URL + 'auth/forgotPassword';
    const payload = {
      email,
    };
    if (url) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (data.message === 'INITIAL_LOGIN') {
          dispatch(
            appendActionMessage({ message: 'Welcome message re-sent to ' + data.destination, type: SnackType.SUCCESS }),
          );
          setShowInitial(true);
        } else if (data.destination) {
          dispatch(appendActionMessage({ message: 'Reset code sent to ' + data.destination, type: SnackType.SUCCESS }));
          setShowReset(true);
        } else {
          if (data.errorMessage) {
            dispatch(appendActionMessage({ message: data.errorMessage, type: SnackType.ERROR }));
            setErrorMessage(data.errorMessage);
          }
        }
      } catch {
        dispatch(appendActionMessage({ message: localeCommon['requestError'], type: SnackType.ERROR }));
      }
    }
  };

  const attemptResetPassword = async (email: string, confirmationCode: string, newPassword: string) => {
    setErrorMessage('');
    if (!email) {
      return dispatch(appendActionMessage({ message: 'No username', type: SnackType.ERROR }));
    }
    if (!confirmationCode) {
      return dispatch(appendActionMessage({ message: 'No confirmation code', type: SnackType.ERROR }));
    }
    if (!newPassword) {
      return dispatch(appendActionMessage({ message: 'No new password', type: SnackType.ERROR }));
    }

    const url = process.env.REACT_APP_BACKEND_URL + 'auth/confirmForgotPassword';
    const payload = {
      email,
      confirmationCode: confirmationCode,
      newPassword: newPassword,
    };
    if (url) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (data.confirmation && data.confirmation === '200') {
          dispatch(appendActionMessage({ message: 'Password reset', type: SnackType.SUCCESS }));
          setShowReset(false);
        } else {
          if (data.errorMessage) {
            dispatch(appendActionMessage({ message: data.errorMessage, type: SnackType.ERROR }));
            setErrorMessage(data.errorMessage);
          }
        }
      } catch {
        dispatch(appendActionMessage({ message: localeCommon['requestError'], type: SnackType.ERROR }));
      }
    }
  };

  const attemptLogin = async (email: string, password: string, rememberMe: boolean) => {
    setErrorMessage('');
    const url = process.env.REACT_APP_BACKEND_URL + 'auth/signIn';
    const payload = {
      email: email,
      password: password,
    };
    if (url) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (data.token) {
          handleTokenLogin(data.token, rememberMe, data.sessionUuid);
        } else if (data.message && data.message === 'FORCE_CHANGE_PASSWORD') {
          setConfirmToken(data.confirmToken);
          setShowConfirmUser(true);
        } else {
          if (data.errorMessage) {
            dispatch(appendActionMessage({ message: data.errorMessage, type: SnackType.ERROR }));
            setErrorMessage(data.errorMessage);
          }
        }
      } catch (e) {
        dispatch(appendActionMessage({ message: localeCommon['requestError'], type: SnackType.ERROR }));
        dispatch(logoutUser());
      }
    }
  };

  const attemptInitialLogin = async (email: string, password: string, rememberMe: boolean) => {
    setErrorMessage('');
    const url = process.env.REACT_APP_BACKEND_URL + 'auth/signInWithConfirm';
    const payload = {
      email: email,
      password: password,
      confirmToken: confirmToken,
    };
    if (url) {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (data.token) {
          handleTokenLogin(data.token, rememberMe, data.sessionUuid);
        } else {
          if (data.errorMessage) {
            dispatch(appendActionMessage({ message: data.errorMessage, type: SnackType.ERROR }));
            setErrorMessage(data.errorMessage);
          }
        }
      } catch {
        dispatch(appendActionMessage({ message: localeCommon['requestError'], type: SnackType.ERROR }));
        dispatch(logoutUser());
      }
    }
  };

  const handleTokenLogin = async (token: string, rememberMe: boolean, sessionUuid: string) => {
    dispatch(setUserData(token, rememberMe, sessionUuid));
    const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
      UserGraphQL.queries.getSelfWithOrganization,
    );
    const user = userData.getSelfWithOrganization.user;
    const role = userData.getSelfWithOrganization.role;
    const organization = userData.getSelfWithOrganization.organization;
    const organizations = userData.getSelfWithOrganization.organizations || [];

    // Set organization data - handle case where user has no organizations
    if (organization) {
      dispatch(setOrganizationData(organization));
    }
    dispatch(setUserDetails({ ...user, role }));
    dispatch(setUserOrganizations(organizations));


    // Locale priority: 1) URL flag (from sessionStorage), 2) user's saved preference, 3) browser language
    const storedUrlLocale = sessionStorage.getItem('urlLocale');
    const urlLocale = storedUrlLocale && ['en', 'es'].includes(storedUrlLocale) ? (storedUrlLocale as UserLocale) : null;
    const browserLang = navigator.language.split('-')[0];
    const browserLocale = ['en', 'es'].includes(browserLang) ? (browserLang as UserLocale) : UserLocale.EN;
    const locale = urlLocale || user.locale || browserLocale;
    dispatch(setLocale(locale));
    sessionStorage.removeItem('urlLocale');

    // Update user locale in backend only if URL flag was provided and differs from saved
    if (urlLocale && user.locale !== urlLocale) {
      try {
        await graphQlClient.request(UserGraphQL.mutations.updateOwnUser, {
          user: { locale: urlLocale },
        });
      } catch (error) {
        console.error('Failed to save user locale preference:', error);
      }
    } 
    // Navigate to the app
    const from = sessionStorage.getItem('redirectUrl') || '/';
    sessionStorage.removeItem('redirectUrl');
    if (from && from !== '/logout') {
      history.push(from);
    } else {
      history.push('/');
    }
  };

  const navigateToGoogleAuth = async () => {
    const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'auth/ssoGoogle');
    const data = await response.json();
    window.location.href = data.url;
  };

  // Handle locale change from the switcher - stores in sessionStorage for use after login
  const handleLocaleChange = (newLocale: UserLocale) => {
    if (newLocale === activeLocale) return;
    dispatch(setLocale(newLocale));
    sessionStorage.setItem('urlLocale', newLocale);
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      <ActionMessages />
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
        <LoginForm
          googleLogin={navigateToGoogleAuth}
          forgotPassword={forgotPassword}
          signIn={attemptLogin}
          resetPassword={attemptResetPassword}
          confirmUserPassword={attemptInitialLogin}
          showReset={showReset}
          showConfirm={showConfirmUser}
          showInitial={showInitial}
          errorMessage={errorMessage}
          prefilledEmail={prefilledEmail}
          prefilledCode={prefilledCode}
        />
        {/* Locale switcher */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
          }}>
          <IconButton
            onClick={() => handleLocaleChange(UserLocale.EN)}
            sx={{
              opacity: activeLocale === 'en' ? 1 : 0.5,
              border: activeLocale === 'en' ? '2px solid #1976d2' : '2px solid transparent',
              borderRadius: '4px',
              padding: '4px',
            }}>
            <img src={EnglishFlag} alt="English" height={20} width={30} />
          </IconButton>
          <IconButton
            onClick={() => handleLocaleChange(UserLocale.ES)}
            sx={{
              opacity: activeLocale === 'es' ? 1 : 0.5,
              border: activeLocale === 'es' ? '2px solid #1976d2' : '2px solid transparent',
              borderRadius: '4px',
              padding: '4px',
            }}>
            <img src={SpanishFlag} alt="Spanish" height={20} width={30} />
          </IconButton>
        </Box>
      </Grid>
      {!isSmallScreen && (
        <Grid item xs={12} md={6}>
          <LoginPromo />
        </Grid>
      )}
    </Grid>
  );
};

export default LoginPage;
